import dayjs from "dayjs";
import { eventBarLook } from "../_components/calendarStatusColors";
import type { BarLook } from "../_components/calendarStatusColors";
import type { AttentionSeverity, BoardShow, ShowLifecycle } from "./boardTypes";

/**
 * The overview speaks the app's own visual language (same as the calendar and
 * the table): black surfaces, pink borders and bands, white bordered controls,
 * the pink→violet gradient for what is selected and the pink glow.
 */
export const THEME = {
  pink: "#D175B6",
  pinkSoft: "rgba(209, 117, 182, 0.35)",
  pinkTint: "rgba(209, 117, 182, 0.18)",
  green: "#00EEC5",
  white: "#FFFFFF",
  black: "#000000",
  gradient: "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%)",
  glow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
  searchGlow: "0px 0px 30px 0px rgba(209, 117, 182, 0.20)",
  rowHover: "#545454",
  line: "rgba(255, 255, 255, 0.08)",
  muted: "rgba(255, 255, 255, 0.8)",
  faint: "rgba(255, 255, 255, 0.55)",
  weekend: "rgba(255, 255, 255, 0.03)",
};

/**
 * Highlighted shows (Osh, 01/10/2026): ShowPhaze neon border, magenta with a purple
 * glow, the same on cards, timeline bars, table rows and calendar bars.
 */
export const NEON = {
  color: "#FF3DF5",
  border: "2px solid #FF3DF5",
  glow: "0 0 6px rgba(255, 61, 245, 0.9), 0 0 18px rgba(143, 0, 255, 0.65)",
};

export type BoardBarLook = BarLook & {
  key: "running" | "upcoming" | "unapproved" | "draft" | "past" | "cancelled";
};

/** Same colors as the calendar bars (Casper, 29/09: past gray, draft / not approved white). */
export function barLook(show: BoardShow): BoardBarLook {
  const eventStatus =
    show.lifecycle === "cancelled"
      ? "Cancelled"
      : show.timing === "past"
        ? "Past"
        : show.timing === "ongoing"
          ? "Ongoing"
          : "Upcoming";
  const look = eventBarLook({ eventStatus, lifecycle: show.lifecycle, isDuplicate: show.isDuplicate });
  const key: BoardBarLook["key"] =
    eventStatus === "Cancelled"
      ? "cancelled"
      : eventStatus === "Past"
        ? "past"
        : show.lifecycle === "draft"
          ? "draft"
          : show.lifecycle !== "approved"
            ? "unapproved"
            : eventStatus === "Ongoing"
              ? "running"
              : "upcoming";
  return { ...look, key };
}

export const SEVERITY_COLORS: Record<AttentionSeverity, string> = {
  critical: "#FD355A",
  warning: "#FACE70",
  info: "#00BFCD",
};

export const READY_COLOR = THEME.green;

/** Color of the readiness number: green when booked, severity color when it needs attention. */
export function readinessColor(show: BoardShow): string {
  if (show.readiness.complete) return READY_COLOR;
  if (show.attention) return SEVERITY_COLORS[show.attention.severity];
  return THEME.white;
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** "$4,000". */
export function money(n: number | null | undefined): string {
  return usd.format(Math.round(n ?? 0));
}

/** "Ended today", "Ended 5 days ago". */
export function endedLabel(show: BoardShow): string {
  const d = show.daysSinceEnd ?? 0;
  if (d <= 0) return "Ended today";
  return `Ended ${d} day${d === 1 ? "" : "s"} ago`;
}

/** Red when a payment is late, pink while it is only waiting, green when closed. */
export function moneyColor(show: BoardShow): string {
  if (show.money?.finalized) return THEME.green;
  return show.money?.issues.length ? SEVERITY_COLORS.critical : THEME.pink;
}

export const LIFECYCLE_LABELS: Record<ShowLifecycle, string> = {
  draft: "Draft",
  newRequest: "New request",
  pendingApproval: "Estimate sent",
  approved: "Approved",
  cancelled: "Cancelled",
};

/** "Running now", "Starts today", "Tomorrow", "In 5 days", "Ended". */
export function whenLabel(show: BoardShow): string {
  const d = show.daysUntilStart;
  if (show.timing === "ongoing") return "Running now";
  if (d === null) return "No date";
  if (show.timing === "past") return "Ended";
  if (d === 0) return "Starts today";
  if (d === 1) return "Tomorrow";
  return `In ${d} days`;
}

/** "Sep 29 – Oct 4" (or a single day). */
export function dateRangeLabel(show: BoardShow): string {
  if (!show.startDay) return "No date";
  const s = dayjs(show.startDay);
  const e = dayjs(show.endDay ?? show.startDay);
  if (s.isSame(e, "day")) return s.format("ddd, MMM D");
  if (s.isSame(e, "month")) return `${s.format("MMM D")} – ${e.format("D")}`;
  return `${s.format("MMM D")} – ${e.format("MMM D")}`;
}

export function placeLabel(show: BoardShow): string {
  return [show.venueCity, show.venueState].filter(Boolean).join(", ");
}

export function clientLabel(show: BoardShow): string {
  return show.client?.companyName || show.client?.name || "";
}

/** White bordered square icon button, like the calendar month arrows. */
export const outlinedIconSx = {
  color: THEME.white,
  border: `1px solid ${THEME.white}`,
  borderRadius: "8px",
  p: "4px",
};
